import { Helmet } from 'react-helmet';
import Lottie from 'react-lottie';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import translate from 'utils/translate';
import LoadingAnimation from 'assets/lottie/loading-vertical-lines.json';
import { Typography } from 'components';
import {
  MANUAL_REVIEW,
  PHONE_VERIFICATION,
  REJECTED_OFFER,
} from 'Router/routes';
import useIovation from 'hooks/useIovation';
import {
  usePrecheckApplicationMutation,
  useCreateApplicationWithoutRiskAndSendGACookiesMutation,
  useAffiliate,
  useApplicationQuery,
  useClientOfferQuery,
} from 'hooks/webapi';
import getClient from 'api/getClient';
import getClientEmail from 'api/getClientEmail';
import getClientPhone from 'api/getClientPhone';
import useCalculator from 'hooks/calculator/useCalculator';
import IdentifyEvent from 'hooks/context/EventCollector/events/IdentifyEvent';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import { useClientApplicationCheckStatusQuery } from 'hooks/webapi/client/useClientApplicationCheckStatusQuery';
import { useClientConstraintsQuery } from 'hooks/webapi/application/useUpsale';
import useSeon from 'hooks/useSeon';
import { logger } from 'utils/logger';
import styles from '../../../../variables.module.scss';
import s from './PersonalDetailsContinue.module.scss';

const LottieOptions = {
  loop: true,
  autoplay: true,
  animationData: LoadingAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

export default function PersonalDetailsContinue() {
  const { mutateAsync: precheckApplication } = usePrecheckApplicationMutation();
  const { mutateAsync: createClientApplicationWithoutRisk } =
    useCreateApplicationWithoutRiskAndSendGACookiesMutation();
  const { sendSeonFraudInfo } = useSeon();
  const { loadAmountFromSessionStorage, amount } = useCalculator();
  const { data: clientConstraintsData, isLoading: clientConstraintsIsLoading } =
    useClientConstraintsQuery();
  const { data: clientOffer } = useClientOfferQuery(
    {
      amount,
      term: clientConstraintsData?.termInterval.defaultValue,
    },
    Boolean(amount) && clientConstraintsIsLoading,
  );

  const [isPollingApplicationStatus, setIsPollingApplicationStatus] =
    useState(false);
  const navigate = useNavigate();
  const { identify } = useEventCollector();
  const { affiliate } = useAffiliate();
  const { ioBlackBox } = useIovation();
  // const authStatus = useAuthStatusMutation();

  useEffect(() => {
    (function checkAuthStatus() {
      sessionStorage.setItem('token', 'kc');
    })();
    loadAmountFromSessionStorage();
  }, []);

  useEffect(() => {
    (async function processClientAfterAuth() {
      const [client, emailData, phone] = await Promise.all([
        getClient(),
        getClientEmail(),
        getClientPhone(),
      ]);

      identify(
        new IdentifyEvent({
          phone,
          email: emailData,
          client,
        }),
      );
    })();
  }, []);

  useEffect(() => {
    (async function createApplicationWithPrecheck() {
      if (!clientOffer) {
        return;
      }

      await createClientApplicationWithoutRisk({
        amount: clientOffer.newPrincipal,
        term: clientOffer.term,
        type: 'WEB',
        source: 'DESKTOP',
        affiliateInfo: affiliate,
        ioBlackBox,
      });

      const precheck = await precheckApplication();

      if (precheck.rejected) {
        navigate(REJECTED_OFFER);
        return;
      }

      // Send SEON fraud info (non-blocking, handle errors gracefully)
      sendSeonFraudInfo().catch((error) => {
        logger.error(
          'SEON fraud info failed, continuing without it',
          undefined,
          error instanceof Error ? error : undefined,
        );
      });

      setIsPollingApplicationStatus(true);
    })();
  }, [affiliate, clientOffer]);

  const { data: applicationCheckStatusData } =
    useClientApplicationCheckStatusQuery(isPollingApplicationStatus, 3000);
  const { data: applicationData } = useApplicationQuery(
    applicationCheckStatusData?.completed,
  );

  useEffect(() => {
    if (!applicationData) {
      return;
    }

    setIsPollingApplicationStatus(false);

    identify(new IdentifyEvent({ application: applicationData }));
    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution === 'MANUAL'
    ) {
      navigate(MANUAL_REVIEW);
    }

    if (
      applicationData.status === 'OPEN' &&
      applicationData.resolution !== 'MANUAL'
    ) {
      navigate(PHONE_VERIFICATION);
      return;
    }

    if (applicationData.resolution === 'REJECTED') {
      navigate(REJECTED_OFFER);
    }
  }, [applicationData]);

  return (
    <>
      <Helmet>
        <title>{translate('waiting.html_title')}</title>
      </Helmet>
      <div className={styles['box-wrapper']}>
        <div className={s.lottie}>
          <Lottie width={200} options={LottieOptions} />
        </div>
        <Typography className={s.title} kind="h1">
          {translate('waiting.title')}
        </Typography>
        <Typography className={s.info}>{translate('waiting.info')}</Typography>
      </div>
    </>
  );
}
